import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { colors } from '../../theme/theme';
import SeatMapCanvas, { SeatOccupancy } from './SeatMapCanvas';
import { RoomOrientation, SeatRow, SlotState } from '../../types/seatLayout';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CHOOSE THE BIKE FOR A STAFF-CREATED BOOKING
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared by every staff flow that puts someone into a seated class — booking a
 * member in manually, and moving one between classes. Both used to let the
 * server auto-assign silently: nobody saw the room, nobody saw who was already
 * in it, and a full room produced a booking with no machine at all.
 *
 * This renders the session's live map: the room as the studio built it, with
 * the riders already in it drawn as taken. The operator picks a free bike and
 * that id travels with the booking.
 *
 * ── WHY THIS IS NOT THE SAME COMPONENT AS SessionSeatMap ────────────────────
 * That one is a read-only board for the class happening now — its job is to
 * answer "who is on bike 14". This one is a control: exactly one free machine
 * can be chosen, and the choice is the point. They share the CANVAS (so a room
 * can never be drawn two different ways) and nothing else.
 *
 * ── WHY IT STILL CANNOT GUARANTEE THE BIKE ──────────────────────────────────
 * Nothing here holds a reservation. Between loading this map and confirming,
 * another booking can take the machine. The partial unique index
 * `session_seat_unique` on (session, seatId) is what actually arbitrates, and
 * the server turns a collision into "Bike 4 was just taken in the new class."
 * Refresh is offered for exactly that reason.
 */

export interface ChosenSeat {
  seatId: string;
  number: string | null;
  row: string;
}

interface Props {
  /**
   * The session the member will END UP in. On a reschedule that is the TARGET
   * class, never the one they are being moved out of.
   */
  sessionId: string;
  value: ChosenSeat | null;
  onChange: (seat: ChosenSeat | null) => void;
  /**
   * Told whether this session actually has a room, once known. The parent
   * gates its confirm button on it: a seated session must not be confirmed
   * without a bike, a roomless one has nothing to pick.
   */
  onRoomResolved?: (hasRoom: boolean) => void;
}

const SessionSeatPicker: React.FC<Props> = ({
  sessionId,
  value,
  onChange,
  onRoomResolved,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const load = useCallback(async () => {
    try {
      // The STAFF map: the same room plus `occupants`, so the desk can see who
      // is already in the class it is moving someone into.
      const res: any = await apiService.getSessionSeatMapStaff(sessionId);
      const payload = res?.data ?? res;
      setData(payload);
      setError(null);
      setLastChecked(new Date());
      onRoomResolved?.(payload?.hasSeatMap === true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || 'Could not load the room',
      );
      // Unknown is NOT "no room": leaving the parent thinking the class is
      // seatless would let it confirm a move with no bike, which is the exact
      // failure this screen exists to stop.
      onRoomResolved?.(true);
    } finally {
      setLoading(false);
    }
  }, [sessionId, onRoomResolved]);

  // A different session is a different room — drop the old choice rather than
  // carrying a seatId that belongs to another room entirely.
  useEffect(() => {
    setLoading(true);
    setData(null);
    onChange(null);
    load();
    // `onChange` is deliberately out of the deps: it is recreated every render
    // by the parent, and including it would clear the operator's pick on every
    // keystroke elsewhere in the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, load]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button size="small" onClick={() => { setLoading(true); load(); }}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  // No room on this class — the member is booked by headcount and there is
  // nothing to choose. Render nothing at all rather than an empty box.
  if (!data?.hasSeatMap) return null;

  const taken = new Set<string>(data.taken ?? []);
  const blocked = new Set<string>(data.blocked ?? []);

  const rows: SeatRow[] = (data.room?.rows ?? []).map((r: any) => ({
    rowId: r.rowId,
    label: r.label,
    surcharge: r.surcharge ?? 0,
    seats: (r.seats ?? []).map((s: any) => ({
      seatId: s.seatId,
      state: (s.state === '-' ? 'gap' : s.state === 'x' ? 'oos' : 'bookable') as SlotState,
      label: s.number ?? null,
      oosReason: s.oosReason ?? null,
      isAccessible: s.isAccessible,
    })),
  }));

  const occupancy: Record<string, SeatOccupancy> = {};
  taken.forEach((id) => (occupancy[id] = 'booked'));
  blocked.forEach((id) => {
    if (!occupancy[id]) occupancy[id] = 'blocked';
  });

  const seatNames: Record<string, string> = {};
  (data.occupants ?? []).forEach((o: any) => {
    if (o.user?.name) seatNames[o.seatId] = o.user.name;
  });

  const bookable = data.room?.bookableCount ?? 0;
  const free = Math.max(0, bookable - taken.size - blocked.size);

  return (
    <Box display="flex" flexDirection="column" gap={1.25}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
          {data.room?.name || 'Room'}
        </Typography>
        <Chip size="small" label={`${taken.size} booked`} color="primary" />
        <Chip size="small" variant="outlined" label={`${free} free`} />
        <Box flex={1} />
        <Typography variant="caption" color="text.secondary">
          {lastChecked ? `Updated ${lastChecked.toLocaleTimeString()}` : ''}
        </Typography>
        <Button
          size="small"
          startIcon={<Refresh />}
          onClick={() => { setLoading(true); load(); }}
        >
          Refresh
        </Button>
      </Box>

      {free === 0 ? (
        // Reachable only as a race — getRescheduleOptions already excludes a
        // session with no free bike — so say so plainly rather than showing a
        // map with nothing to click.
        <Alert severity="warning">
          Every bike in this class has just been taken. Refresh, or pick a
          different session.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ py: 0.25 }}>
          Pick the bike this member will ride. Booked machines are theirs
          already — hover one to see who.
        </Alert>
      )}

      <SeatMapCanvas
        rows={rows}
        occupancy={occupancy}
        occupantNames={seatNames}
        frontLabel={data.room?.frontLabel ?? 'INSTRUCTOR'}
        backLabel={data.room?.backLabel ?? 'ENTRANCE'}
        orientation={(data.room?.orientation ?? 'instructor_top') as RoomOrientation}
        selectable
        selectedSeatId={value?.seatId ?? null}
        onSelectSeat={(seat) => onChange(seat)}
      />

      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Key label="Chosen" sx={{ background: colors.success, borderColor: colors.success }} />
        <Key label="Booked" sx={{ background: colors.primaryDark, borderColor: colors.primaryDark }} />
        <Key label="Free" sx={{ background: colors.white, borderColor: colors.divider }} />
        <Key
          label="Out of service"
          sx={{ background: colors.white, borderColor: colors.warning, borderStyle: 'dashed' }}
        />
        <Key label="Unavailable" sx={{ background: colors.greyText3, borderColor: colors.greyText2 }} />
      </Box>

      <Typography sx={{ fontSize: 12, color: value ? colors.success : colors.textSecondary, fontWeight: value ? 700 : 400 }}>
        {value
          ? `Bike ${value.number ?? value.seatId} · row ${value.row} selected`
          : 'No bike chosen yet.'}
      </Typography>
    </Box>
  );
};

const Key: React.FC<{ label: string; sx: any }> = ({ label, sx }) => (
  <Box display="flex" alignItems="center" gap={0.6}>
    <Box sx={{ width: 13, height: 13, borderRadius: 0.75, border: '1.5px solid', ...sx }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export default SessionSeatPicker;
